import { useNavigate } from "react-router-dom";
import type { ProjectItem } from "@/lib/projects-clients";
import { type ProjectTicketContext, formatCurrency, mapApiTypeToLabel } from "@/lib/tickets";

type ProjectContextSummaryProps = {
  project: ProjectItem;
  context: ProjectTicketContext | null | undefined;
  compact?: boolean;
};

const formatProjectStatus = (status?: ProjectItem["status"]) =>
  String(status ?? "ACTIVE")
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatTicketStatus = (status?: string | null) =>
  status
    ? status
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "None";

const ticketStatusClass = (status?: string | null) => {
  switch (status) {
    case "APPROVED":
      return "bg-status-approved-bg text-status-approved";
    case "REJECTED":
      return "bg-destructive/10 text-destructive";
    case "NEEDS_CLARIFICATION":
      return "bg-status-signature-bg text-status-signature";
    case "PENDING_APPROVAL":
      return "bg-accent/10 text-accent";
    case "DRAFT":
      return "bg-slate-100 text-slate-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

export function ProjectContextSummary({
  project,
  context,
  compact = false,
}: ProjectContextSummaryProps) {
  const navigate = useNavigate();

  return (
    <div
      className={`rounded-lg border border-slate-200/90 bg-slate-50/80 ${
        compact ? "space-y-3 p-3 shadow-none" : "space-y-4 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]"
      }`}
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div className="space-y-0.5">
          <h3 className={`${compact ? "text-sm" : "text-lg"} font-semibold text-[#0C1F4F]`}>{project.name}</h3>
          <p className="text-sm font-medium text-[#243B68]">{project.client?.name ?? "No client linked"}</p>
        </div>
        <span className="inline-flex w-fit rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
          {formatProjectStatus(project.status)}
        </span>
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#51698F]">Tickets</p>
          <p className="text-sm font-medium text-[#0C1F4F]">
            {context?.tickets.length ?? 0} total
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#51698F]">Last ticket</p>
          <p className="text-sm font-medium text-[#0C1F4F]">
            {context?.lastTicketCreatedAt
              ? new Intl.DateTimeFormat("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                }).format(new Date(context.lastTicketCreatedAt))
              : "No tickets"}
          </p>
        </div>
      </div>

      <div className={`space-y-2 rounded-lg border border-slate-200/90 bg-white/90 ${compact ? "p-2.5 shadow-none" : "p-3 shadow-[0_6px_18px_rgba(15,23,42,0.04)]"}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#51698F]">Project tickets</p>
          <p className="text-right text-xs font-semibold text-accent">
            Current contract {formatCurrency(context?.approvedTotal ?? context?.currentContractTotal ?? project.approvedTotal ?? 0)} • Pending {formatCurrency(context?.pendingChangesTotal ?? 0)}
          </p>
        </div>
        <div className="space-y-2">
          {(context?.tickets ?? []).length ? (
            (context?.tickets ?? []).map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => navigate(`/tickets/${ticket.id}`)}
                className="flex w-full flex-col gap-1 rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2 text-left transition-colors hover:bg-white md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-[#0C1F4F]">{ticket.title || mapApiTypeToLabel(ticket.type)}</p>
                  <p className="text-xs font-medium text-[#51698F]">
                    {mapApiTypeToLabel(ticket.type)} • {new Intl.DateTimeFormat("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }).format(new Date(ticket.createdAt))}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2.5 py-1 font-medium ring-1 ring-black/5 ${ticketStatusClass(ticket.status)}`}>
                    {formatTicketStatus(ticket.status)}
                  </span>
                  <span className="font-medium text-[#0C1F4F]">{formatCurrency(ticket.totalAmount)}</span>
                </div>
              </button>
            ))
          ) : (
            <p className="text-sm text-slate-500">No tickets created for this project yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
